// src/pages/Cart.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import ConfirmModal from "../components/Common/ConfirmModal";
import PromotionExpiryWarning from "../components/Common/PromotionExpiryWarning";
import { formatPrice } from "../utils/format";
import {
  FiTrash2,
  FiShoppingCart,
  FiArrowLeft,
  FiCreditCard,
  FiRefreshCw,
  FiTag,
} from "react-icons/fi";
import "../styles/cart.css";

const Cart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    cartItems,
    cartSubtotal,
    cartTax,
    cartTaxEnabled,
    cartTaxRate,
    cartTotal,
    cartSavings,
    cartOriginalTotal,
    loading,
    loadingItems,
    deletingItemId,
    expiringPromotions,
    updateQuantity,
    removeFromCart,
    clearCart,
    fetchCartFromServer,
  } = useCart();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localCartItems, setLocalCartItems] = useState(cartItems);
  const [removingItemId, setRemovingItemId] = useState(null);
  const [updatingQuantityId, setUpdatingQuantityId] = useState(null);

  // Modal states
  const [showClearCartModal, setShowClearCartModal] = useState(false);
  const [showRemoveItemModal, setShowRemoveItemModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  // Sync local cart items with context
  useEffect(() => {
    setLocalCartItems(cartItems);
  }, [cartItems]);

  // ✅ FIXED: fetch cart once on mount only.
  // Do NOT depend on location.key or fetchCartFromServer — both cause
  // re-fetches on every navigation and re-render, which visually resets
  // the scroll position via content reflow.
  useEffect(() => {
    let cancelled = false;

    const refreshCart = async () => {
      if (cancelled) return;
      setIsRefreshing(true);
      await fetchCartFromServer();
      if (!cancelled) setIsRefreshing(false);
    };

    refreshCart();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  // ✅ FIXED: separate effect for tab visibility, setup once.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCartFromServer();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle quantity change with loading state
  const handleQuantityChange = async (productId, newQuantity) => {
    if (newQuantity < 1) {
      const item = localCartItems.find(
        (item) => (item.ProductID || item.productId || item.id) === productId,
      );
      setItemToRemove(item);
      setShowRemoveItemModal(true);
      return;
    }

    setUpdatingQuantityId(productId);

    const updatedItems = localCartItems.map((item) => {
      const itemId = item.ProductID || item.productId || item.id;
      if (itemId === productId) {
        return { ...item, Quantity: newQuantity };
      }
      return item;
    });
    setLocalCartItems(updatedItems);

    try {
      await updateQuantity(productId, newQuantity);
    } catch (error) {
      setLocalCartItems(cartItems);
    } finally {
      setUpdatingQuantityId(null);
    }
  };

  const handleRemoveItem = (productId) => {
    const item = localCartItems.find(
      (item) => (item.ProductID || item.productId || item.id) === productId,
    );
    setItemToRemove(item);
    setShowRemoveItemModal(true);
  };

  const confirmRemoveItem = async () => {
    if (!itemToRemove) return;

    const itemId =
      itemToRemove.ProductID || itemToRemove.productId || itemToRemove.id;
    setRemovingItemId(itemId);
    setShowRemoveItemModal(false);

    setTimeout(async () => {
      try {
        await removeFromCart(itemId);
      } catch (error) {
        setRemovingItemId(null);
      } finally {
        setItemToRemove(null);
      }
    }, 300);
  };

  const handleClearCart = () => {
    if (localCartItems.length === 0) return;
    setShowClearCartModal(true);
  };

  const confirmClearCart = async () => {
    setShowClearCartModal(false);
    const allItemIds = localCartItems.map(
      (item) => item.ProductID || item.productId || item.id,
    );
    allItemIds.forEach((id) => setRemovingItemId(id));
    setTimeout(async () => {
      await clearCart();
      setRemovingItemId(null);
    }, 300);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchCartFromServer();
    setIsRefreshing(false);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) return;
    navigate("/checkout");
  };

  // Check if item has a promotion
  const hasPromotion = (item) => {
    return (
      item.OriginalPrice &&
      item.OriginalPrice > (item.UnitPrice || item.Price || 0)
    );
  };

  // Get discount percentage
  const getDiscountPercent = (item) => {
    if (!hasPromotion(item)) return 0;
    const original = parseFloat(item.OriginalPrice) || 0;
    const current = parseFloat(item.UnitPrice || item.Price) || 0;
    if (original === 0) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  if (loading && !isRefreshing && localCartItems.length === 0) {
    return (
      <div className="cart-loading">
        <div className="spinner"></div>
        <p>Loading your cart...</p>
      </div>
    );
  }

  if ((!localCartItems || localCartItems.length === 0) && !loading) {
    return (
      <div className="cart-empty">
        <div className="cart-empty-icon">
          <FiShoppingCart />
        </div>
        <h2>Your cart is empty</h2>
        <p>Looks like you haven't added any items to your cart yet.</p>
        <Link to="/shop" className="btn-shop-now">
          Start Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="cart-page">
      {/* ✅ Promotion Expiry Warning */}
      {expiringPromotions.length > 0 && (
        <PromotionExpiryWarning items={localCartItems} />
      )}

      <ConfirmModal
        isOpen={showClearCartModal}
        onClose={() => setShowClearCartModal(false)}
        onConfirm={confirmClearCart}
        title="Clear Cart"
        message="Are you sure you want to remove all items from your cart? This action cannot be undone."
        confirmText="Yes, Clear All"
        cancelText="Cancel"
        type="danger"
      />

      <ConfirmModal
        isOpen={showRemoveItemModal}
        onClose={() => {
          setShowRemoveItemModal(false);
          setItemToRemove(null);
        }}
        onConfirm={confirmRemoveItem}
        title="Remove Item"
        message={`Are you sure you want to remove "${itemToRemove?.Name || "this item"}" from your cart?`}
        confirmText="Yes, Remove"
        cancelText="Cancel"
        type="warning"
      />

      <div className="cart-container">
        <div className="cart-header-section">
          <h1>Shopping Cart</h1>
          <button
            onClick={handleRefresh}
            className="btn-refresh"
            disabled={isRefreshing}
          >
            <FiRefreshCw className={isRefreshing ? "spin" : ""} />
            {isRefreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="cart-grid">
          {/* Cart Items */}
          <div className="cart-items">
            <div className="cart-header">
              <div className="cart-header-product">Product</div>
              <div className="cart-header-price">Price</div>
              <div className="cart-header-quantity">Quantity</div>
              <div className="cart-header-total">Total</div>
              <div className="cart-header-action"></div>
            </div>

            {localCartItems.map((item) => {
              const itemPrice =
                item.UnitPrice || item.Price || item.SellingPrice || 0;
              const originalPrice = item.OriginalPrice || itemPrice;
              const itemTotal = itemPrice * (item.Quantity || 0);
              const originalTotal = originalPrice * (item.Quantity || 0);
              const itemId = item.ProductID || item.productId || item.id;
              const isDeleting =
                removingItemId === itemId || deletingItemId === itemId;
              const isLoading =
                loadingItems[itemId] || updatingQuantityId === itemId;
              const hasPromo = hasPromotion(item);
              const discountPercent = getDiscountPercent(item);
              const promotion = item.Promotion;

              return (
                <div
                  key={itemId}
                  className={`cart-item ${isDeleting ? "removing" : ""} ${hasPromo ? "has-promotion" : ""}`}
                >
                  <div className="cart-item-product">
                    <div className="cart-item-image">
                      <img
                        src={item.ImageUrl || "/images/placeholder.jpg"}
                        alt={item.Name || "Product"}
                        onError={(e) => {
                          e.target.src = "/images/placeholder.jpg";
                        }}
                      />
                      {hasPromo && (
                        <div className="cart-item-discount-badge">
                          -{discountPercent}%
                        </div>
                      )}
                    </div>
                    <div className="cart-item-info">
                      <h3>{item.Name || "Product"}</h3>
                      {item.SKU && (
                        <span className="cart-item-sku">SKU: {item.SKU}</span>
                      )}
                      {promotion?.Code && (
                        <div className="cart-item-promo-code">
                          <FiTag size={12} />
                          <span>{promotion.Code}</span>
                          {promotion.DiscountType === "Percentage" && (
                            <span className="promo-discount-value">
                              ({promotion.DiscountValue}% OFF)
                            </span>
                          )}
                          {promotion.EndDate && (
                            <span className="promo-expiry-date">
                              Expires:{" "}
                              {new Date(promotion.EndDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="cart-item-price">
                    {hasPromo ? (
                      <>
                        <span className="original-price">
                          {formatPrice(originalPrice)}
                        </span>
                        <span className="current-price promotion-price">
                          {formatPrice(itemPrice)}
                        </span>
                      </>
                    ) : (
                      <span className="current-price">
                        {formatPrice(itemPrice)}
                      </span>
                    )}
                  </div>

                  <div className="cart-item-quantity">
                    <input
                      type="number"
                      min="1"
                      value={item.Quantity || 1}
                      onChange={(e) =>
                        handleQuantityChange(itemId, parseInt(e.target.value))
                      }
                      disabled={isLoading || isDeleting}
                      className="quantity-input"
                    />
                  </div>

                  <div className="cart-item-total">
                    {hasPromo ? (
                      <>
                        <span className="original-total">
                          {formatPrice(originalTotal)}
                        </span>
                        <span className="current-total promotion-total">
                          {formatPrice(itemTotal)}
                        </span>
                      </>
                    ) : (
                      <span>{formatPrice(itemTotal)}</span>
                    )}
                  </div>

                  <div className="cart-item-action">
                    <button
                      onClick={() => handleRemoveItem(itemId)}
                      className={`remove-btn ${isLoading || isDeleting ? "loading" : ""}`}
                      disabled={isLoading || isDeleting}
                      aria-label="Remove item"
                    >
                      {isLoading || isDeleting ? (
                        <div className="spinner-small"></div>
                      ) : (
                        <FiTrash2 />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}

            <div className="cart-actions">
              <button
                onClick={handleClearCart}
                className="btn-clear-cart"
                disabled={localCartItems.length === 0}
              >
                Clear Cart
              </button>
            </div>
          </div>

          {/* Order Summary with Savings */}
          <div className="cart-summary">
            <h2>Order Summary</h2>
            <div className="summary-details">
              {cartOriginalTotal > cartSubtotal && (
                <div className="summary-row original">
                  <span>Original Price:</span>
                  <span className="original-price">
                    {formatPrice(cartOriginalTotal)}
                  </span>
                </div>
              )}
              <div className="summary-row">
                <span>Subtotal:</span>
                <span className="animated-price">
                  {formatPrice(cartSubtotal)}
                </span>
              </div>
              {cartSavings > 0 && (
                <div className="summary-row savings">
                  <span>You Save:</span>
                  <span className="savings-amount">
                    -{formatPrice(cartSavings)}
                  </span>
                </div>
              )}
              {cartTaxEnabled && (
                <div className="summary-row">
                  <span>VAT ({cartTaxRate}%):</span>
                  <span className="animated-price">{formatPrice(cartTax)}</span>
                </div>
              )}
              <div className="summary-row total">
                <span>Total:</span>
                <span className="animated-price total-price">
                  {formatPrice(cartTotal)}
                </span>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              className="btn-checkout"
              disabled={localCartItems.length === 0}
            >
              <FiCreditCard /> Proceed to Checkout
            </button>

            <Link to="/shop" className="btn-continue-shopping">
              <FiArrowLeft /> Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
